// @ts-ignore
import styles from "./ActionItemComment.module.scss";

// React
import { useState, useEffect, useMemo, useRef } from "react";

// Redux
import { useDispatch, useSelector } from "react-redux";

// Icons
import { ReactComponent as SearchIcon } from "assets/svg/Search.svg";
import { ReactComponent as LikeIcon } from "assets/svg/like.svg";
import { ReactComponent as EditIcon } from "assets/svg/editSmall.svg";
import { ReactComponent as TrashIcon } from "assets/svg/trashSmall.svg";
import { ReactComponent as UndoIcon } from "assets/svg/undo.svg";
import { LoadingOutlined } from "@ant-design/icons";

// Ant Design
import { Spin } from "antd";

// Actions
import {
  getActivityLog,
  createActionItemComment,
  getCommentOfSpecificActionItem,
  likeComment,
  unlikeComment,
  updateComment,
  replyComment,
  // @ts-ignore
} from "modules/actions/PlanActions";

// Data
import {
  categoryStatusOptions,
  statusOptions,
  priorityOptions,
} from "../../scenes/ProjectPlanner/components/GoalSettings/data";

// Utils
import { formatRelativeTime } from "../../utils/helper";
// @ts-ignore
import { useUser } from "utils/utility";

// Components
import { Mention, MentionsInput } from "react-mentions";

// Types
import {
  ActionItemCommentProps,
  CommentType,
  UserDetail,
  ActivityLogType,
  ChangeResult,
  RootState,
  MentionChangeEvent,
  MergedActivityItem,
} from "../../@types/comments";

const formatCommentWithMentions = (text: string): JSX.Element => {
  if (!text) return <></>;

  const mentionRegex = /@\[(.*?)\]\((\d+)\)/g;

  if (!text.match(mentionRegex)) {
    return <>{text}</>;
  }

  const placeholder = "###MENTION###";
  const mentions: Array<{ email: string; id: string }> = [];

  const textWithPlaceholders = text.replace(mentionRegex, (match) => {
    const emailMatch = match.match(/@\[(.*?)\]\((\d+)\)/);
    if (emailMatch && emailMatch[1] && emailMatch[2]) {
      mentions.push({
        email: emailMatch[1],
        id: emailMatch[2],
      });
      return placeholder;
    }
    return match;
  });

  const parts = textWithPlaceholders.split(placeholder);
  const result: JSX.Element[] = [];

  parts.forEach((part, index) => {
    if (part) {
      result.push(<span key={`text-${index}`}>{part}</span>);
    }

    if (index < mentions.length) {
      result.push(
        <span key={`mention-${index}`} className={styles.mentionTag}>
          @{mentions[index].email}
        </span>
      );
    }
  });

  return <>{result}</>;
};

function CommentItem({
  comment,
  isReplyComment,
  onReply,
  onEdit,
  onLike,
  onDelete,
}: {
  comment: CommentType;
  isReplyComment: boolean;
  onReply: (comment: CommentType) => void;
  onEdit: (comment: CommentType) => void;
  onLike: (
    commentId: string,
    isLiked: boolean,
    handleLikeCallback: () => void
  ) => void;
  onDelete: (
    comment: CommentType,
    handleDeleteCommentCallback: () => void
  ) => void;
}) {
  const [isLike, setIsLike] = useState(false);
  const [totalLikes, setTotalLikes] = useState(comment?.total_like || 0);
  const [isDeletedLoading, setIsDeletedLoading] = useState(false);
  const [isLikeLoading, setIsLikeLoading] = useState(false);
  const { projectUser: projectAssignee } = useSelector(
    (state: RootState) => state.user.projectOwners
  );

  const user = useUser();

  const findUser =
    projectAssignee?.find((users: UserDetail) => users?.email === user?.email)
      ?.id ?? null;

  const isUserLiked =
    comment?.like_user && comment?.like_user?.length > 0
      ? comment?.like_user?.some((likeUser) => likeUser === findUser)
      : false;

  const isOwnComment = comment?.user_detail?.email === user?.email;

  const userName = comment?.user_detail?.first_name
    ? `${comment.user_detail.first_name} ${comment.user_detail.last_name}`
    : comment?.user_detail?.name || comment?.user_detail?.email;

  const handleLikeCallback = () => {
    setIsLikeLoading(false);
  };

  const handleLike = (commentId: string): void => {
    if (isOwnComment) return;

    setIsLikeLoading(true);
    setIsLike(!isLike);
    setTotalLikes(isLike ? totalLikes - 1 : totalLikes + 1);
    onLike(commentId, isLike, handleLikeCallback);
  };

  const handleDeleteCommentCallback = () => {
    setIsDeletedLoading(false);
  };

  const handleDeleteComment = (comment: CommentType) => {
    if (isDeletedLoading) return;
    setIsDeletedLoading(true);
    onDelete(comment, handleDeleteCommentCallback);
  };

  useEffect(() => {
    if (comment?.total_like) {
      setTotalLikes(comment?.total_like);
    }
  }, [comment?.total_like]);

  useEffect(() => {
    setIsLike(isUserLiked);
  }, [isUserLiked]);

  return (
    <div
      className={`${styles.comment} ${
        isReplyComment ? styles.replyComment : ""
      }`}
    >
      <span className={styles.commentHeader}>
        <div className={styles.profileDetails}>
          <div className={styles.profileImage}>
            {comment.user_detail?.profile_image ? (
              <img
                src={comment.user_detail?.profile_image || ""}
                alt="profile"
              />
            ) : (
              (userName ?? "?").split(" ")[0].charAt(0)
            )}
          </div>
          <div className={styles.profileName}>
            <h4>{userName}</h4>
            <span>{comment.user_detail?.email}</span>
          </div>
        </div>

        <span className={styles.timestamp}>
          {formatRelativeTime(comment.timestamp || comment.created_at)}
        </span>
      </span>

      <p className={styles.commentContent}>
        {formatCommentWithMentions(comment.context)}
      </p>

      <div className={styles.commentFooter}>
        <div>
          {isLikeLoading ? (
            <LoadingOutlined spin />
          ) : (
            <div
              className={`${styles.likeContainer} ${
                isOwnComment ? styles.disabledLike : ""
              }`}
              onClick={() => {
                if (isDeletedLoading || isOwnComment) return;
                handleLike(comment.id);
              }}
              style={{
                cursor: isOwnComment ? "not-allowed" : "pointer",
              }}
            >
              <LikeIcon
                style={{
                  color: isLike ? "#1271a6" : "#a3a3a3",
                  opacity: isOwnComment ? 0.5 : 1,
                  cursor: isOwnComment ? "not-allowed" : "pointer",
                }}
              />

              <span
                className={styles.likeCount}
                style={{
                  color: isLike ? "#1271a6" : "#a3a3a3",
                  opacity: isOwnComment ? 0.5 : 1,
                }}
              >
                {totalLikes}
              </span>
            </div>
          )}

          <span
            className={styles.reply}
            onClick={() => {
              if (isDeletedLoading) return;
              onReply(comment);
            }}
          >
            Reply
          </span>
        </div>
        {comment?.isOwner && (
          <div>
            <EditIcon
              className={styles.actionIcon}
              onClick={() => {
                if (isDeletedLoading) return;
                onEdit(comment);
              }}
            />

            {isDeletedLoading ? (
              <LoadingOutlined spin />
            ) : (
              <TrashIcon
                className={styles.actionIcon}
                onClick={() => {
                  if (isDeletedLoading) return;
                  handleDeleteComment(comment);
                }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const ActionItemComment = ({
  actionItemId,
}: ActionItemCommentProps): JSX.Element => {
  const dispatch = useDispatch();
  const mentionsInputRef = useRef<HTMLTextAreaElement>(null);

  const [commentText, setCommentText] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [editCommentId, setEditCommentId] = useState<string | null>(null);
  const [isReply, setIsReply] = useState<boolean>(false);
  const [replyToCommentId, setReplyToCommentId] = useState<string | null>(null);
  const [replyToUser, setReplyToUser] = useState<string | null>(null);
  const [taggedUsers, setTaggedUsers] = useState<number[]>([]);
  const [recentlyDeleted, setRecentlyDeleted] = useState<{
    comment: CommentType;
    id: string;
  } | null>(null);
  const [undoTimer, setUndoTimer] = useState<NodeJS.Timeout | null>(null);
  const [deletedCommentIds, setDeletedCommentIds] = useState<string[]>([]);
  const [undoLoading, setUndoLoading] = useState<boolean>(false);

  const { projectUser: projectAssignee } = useSelector(
    (state: RootState) => state.user.projectOwners
  );

  const {
    commentOfSpecificActionItem,
    activityLog,
    activityLoading,
    commentOfSpecificActionItemLoading,
    planDetails,
  } = useSelector((state: RootState) => state.plan.plan);

  const projectUser = planDetails?.resource?.people;

  const user = useUser();

  const getFieldLabel = (field: string): string => {
    const fieldLabels: Record<string, string> = {
      status: "Status",
      weight: "Weight",
      plan_progress: "Progress",
      description: "Description",
      title: "Title",
      start_line: "Start Date",
      dead_line: "Due Date",
      assignee: "Assignee",
    };

    return fieldLabels[field] || field;
  };

  const formatStatusValue = (value: number): string => {
    const status = statusOptions.find((status) => status.value === value);
    return status?.label || `Status ${value}`;
  };

  const getChangeDetails = (
    oldData: Record<string, unknown> | null,
    newData: Record<string, unknown> | null
  ): ChangeResult => {
    if (!oldData || !newData) return { changes: [], changeType: null };

    const changes: string[] = [];
    let changeType: string | null = null;

    const ignoreFields: string[] = [
      "end_line_change_timestamp",
      "start_line_change_timestamp",
      "progress_last_update",
      "start_grace_time_left",
      "start_change_timestamp",
      "category_progress",
      "manual_override",
      "is_start_locked",
      "metric_unit",
      "category_details",
      "updated_at",
    ];

    Object.keys(newData).forEach((field) => {
      if (ignoreFields.includes(field)) return;

      if (field in oldData && oldData[field] !== newData[field]) {
        const fieldLabel = getFieldLabel(field);
        if (field === "status") {
          changeType = "status";
          changes.push(
            `changed ${fieldLabel} from "${formatStatusValue(
              oldData[field] as number
            )}" to "${formatStatusValue(newData[field] as number)}"`
          );
        } else if (field === "name_html") {
          changeType = "text";
          const oldVal =
            oldData[field] === null || oldData[field] === ""
              ? "null"
              : `"${oldData[field]}"`;
          const newVal =
            newData[field] === null || newData[field] === ""
              ? "null"
              : `"${newData[field]}"`;
          changes.push(`changed name from ${oldVal} to ${newVal}`);
        } else if (field === "description") {
          const isAddDescription =
            oldData[field] === null || oldData[field] === "";

          changeType = "text";
          changes.push(
            `${isAddDescription ? "Added" : "Updated"} description to ${
              newData[field] || " "
            }`
          );
        } else if (field === "responsible_person") {
          const oldUser = projectAssignee?.find(
            (user: UserDetail) => user?.id === oldData[field]
          )?.email;

          const newUser = projectAssignee?.find(
            (user: UserDetail) => user?.id === newData[field]
          )?.email;

          changeType = "text";
          changes.push(`changed Assignee from "${oldUser}" to "${newUser}"`);
        } else if (field === "weight" || field === "plan_progress") {
          changeType = "numeric";
          const oldVal =
            oldData[field] === null || oldData[field] === ""
              ? "null"
              : oldData[field];
          const newVal =
            newData[field] === null || newData[field] === ""
              ? "null"
              : newData[field];
          changes.push(
            `changed ${
              field === "plan_progress" ? "Plan Progress" : "Weight"
            } from ${oldVal} to ${newVal}`
          );
        } else {
          changeType = "text";
          const oldVal =
            oldData[field] === null || oldData[field] === ""
              ? "null"
              : `"${oldData[field]}"`;
          const newVal =
            newData[field] === null || newData[field] === ""
              ? "null"
              : `"${newData[field]}"`;
          changes.push(`changed ${fieldLabel} from ${oldVal} to ${newVal}`);
        }
      }
    });

    return { changes, changeType };
  };

  const organizeComments = (comments: CommentType[]): CommentType[] => {
    if (!comments || !Array.isArray(comments) || comments.length === 0)
      return [];
    const parentComments: CommentType[] = [];
    const childCommentsMap: Record<string, CommentType[]> = {};

    comments?.forEach((comment) => {
      if (comment.parent_comment) {
        if (!childCommentsMap[comment.parent_comment]) {
          childCommentsMap[comment.parent_comment] = [];
        }
        childCommentsMap[comment.parent_comment].push({
          ...comment,
          type: "comment",
          timestamp: comment.created_at,
          isOwner: comment.user_detail?.email === user?.email,
        });
      } else {
        parentComments.push({
          ...comment,
          type: "comment",
          timestamp: comment.created_at,
          isOwner: comment.user_detail?.email === user?.email,
        });
      }
    });

    parentComments.forEach((parent) => {
      parent.replies = childCommentsMap[parent.id] || [];
    });

    return parentComments;
  };

  const mergedActivities = useMemo(() => {
    if (!commentOfSpecificActionItem || !activityLog) return [];

    const organizedComments = organizeComments(
      commentOfSpecificActionItem || []
    );

    const activityLogs = Array.isArray(activityLog) ? activityLog : [];

    const formattedLogs = activityLogs
      .map((log) => ({
        ...log,
        type: "activity",
        timestamp: log.created_at,
        user_detail: log?.user_detail,
        changeDetails: getChangeDetails(log.old_data, log.new_data)?.changes,
        changeType: getChangeDetails(log.old_data, log.new_data)?.changeType,
      }))
      .filter((log) => {
        if (log.action === "create") {
          return true;
        }

        if (!log.changeDetails || log.changeDetails.length === 0) {
          return false;
        }

        if (log.changeDetails && log.changeDetails.length === 1) {
          const change = log.changeDetails[0];
          const isLabelOnlyChange =
            change.includes('changed label from null to "custom"') ||
            change.includes('changed label from "custom" to null') ||
            change.includes("changed label from null to null");

          if (isLabelOnlyChange) {
            return false;
          }
        }

        return true;
      });

    const allItems = [...organizedComments, ...formattedLogs];

    return allItems.sort((a, b) => {
      const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return timeA - timeB;
    });
  }, [commentOfSpecificActionItem, activityLog, projectUser, user?.email]);

  const filteredActivities = useMemo(() => {
    if (!search.trim()) return mergedActivities;

    const searchLower = search.toLowerCase();

    return mergedActivities?.filter((item: MergedActivityItem) => {
      const userName = item?.user_detail?.first_name
        ? `${item.user_detail.first_name} ${item.user_detail.last_name}`
        : item?.user_detail?.name || item?.user_detail?.email || "";

      if (item.type === "comment") {
        const commentItem = item as CommentType;
        const commentMatches =
          userName.toLowerCase().includes(searchLower) ||
          (commentItem.context &&
            commentItem.context.toLowerCase().includes(searchLower));

        if (commentMatches) return true;

        if (commentItem.replies && commentItem.replies.length > 0) {
          return commentItem.replies.some((reply: CommentType) => {
            const replyUserName = reply.user_detail?.first_name
              ? `${reply.user_detail.first_name} ${reply.user_detail.last_name}`
              : reply.user_detail?.name || reply.user_detail?.email || "";

            return (
              replyUserName.toLowerCase().includes(searchLower) ||
              (reply.context &&
                reply.context.toLowerCase().includes(searchLower))
            );
          });
        }

        return false;
      }

      if (item.type === "activity") {
        const activityItem = item as ActivityLogType;
        if (userName.toLowerCase().includes(searchLower)) return true;

        if (
          activityItem.action === "create" &&
          "created this action item".toLowerCase().includes(searchLower)
        ) {
          return true;
        }

        if (
          activityItem.changeDetails &&
          activityItem.changeDetails.length > 0
        ) {
          return activityItem.changeDetails.some((change: string) =>
            change.toLowerCase().includes(searchLower)
          );
        }
      }

      return false;
    });
  }, [mergedActivities, search]);

  const handleMentionChange = (e: MentionChangeEvent): void => {
    let value = e.target.value;

    value = value.replace(/^\s+/, "");

    if (value.length > 500) {
      setCommentText(value.slice(0, 500));
    } else {
      setCommentText(value);
    }

    const mentionRegex = /@\[.*?\]\((\d+)\)/g;
    const matches = value.match(mentionRegex);

    if (matches && matches.length > 0) {
      const userIds = matches
        .map((match) => {
          const idMatch = match.match(/@\[.*?\]\((\d+)\)/);
          return idMatch ? parseInt(idMatch[1], 10) : null;
        })
        .filter((id) => id !== null) as number[];

      setTaggedUsers(userIds);
    } else {
      setTaggedUsers([]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent): void => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (commentText.trim() && !loading) {
        handleSendComment();
      }
    }
  };

  const handleClose = (): void => {
    setIsEdit(false);
    setEditCommentId(null);
    setIsReply(false);
    setReplyToCommentId(null);
    setReplyToUser(null);
    setCommentText("");
    setTaggedUsers([]);
    setLoading(false);
  };

  const handleSendComment = (): void => {
    setLoading(true);

    try {
      if (isEdit && editCommentId) {
        const payload = {
          id: editCommentId,
          context: commentText,
          tagged_users: taggedUsers,
        };
        dispatch(updateComment(editCommentId, payload, handleClose));
      } else if (isReply && replyToCommentId) {
        const payload = {
          action_item: actionItemId,
          context: commentText,
          parent_comment: replyToCommentId,
          tagged_users: taggedUsers,
        };
        dispatch(replyComment(payload, handleClose));
      } else {
        const payload = {
          action_item: actionItemId,
          context: commentText,
          tagged_users: taggedUsers,
        };
        dispatch(createActionItemComment(payload, handleClose));
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  const handleEditComment = (comment: CommentType): void => {
    cancelReply();

    setIsEdit(true);
    setEditCommentId(comment.id);
    setCommentText(comment.context);
  };

  const handleReplyToComment = (comment: CommentType): void => {
    cancelEdit();

    setIsReply(true);
    setReplyToCommentId(comment.parent_comment || comment.id);

    const userName = comment.user_detail?.first_name
      ? `${comment.user_detail.first_name} ${comment.user_detail.last_name}`
      : comment.user_detail?.name || comment.user_detail?.email || "";

    setReplyToUser(userName);

    setCommentText("");
    setTaggedUsers([]);
    mentionsInputRef.current?.focus();
  };

  const cancelEdit = (): void => {
    setIsEdit(false);
    setEditCommentId(null);
    setCommentText("");
    setTaggedUsers([]);
  };

  const cancelReply = (): void => {
    setIsReply(false);
    setReplyToCommentId(null);
    setReplyToUser(null);
    setCommentText("");
    setTaggedUsers([]);
  };

  const handleLikeComment = (
    commentId: string,
    isLiked: boolean,
    handleLikeCallback: () => void
  ): void => {
    try {
      const payload = {
        comment: commentId,
        like_type: "smile",
      };

      if (isLiked) {
        dispatch(unlikeComment(payload, handleLikeCallback));
      } else {
        dispatch(likeComment(payload, handleLikeCallback));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteCallback = (
    comment: CommentType,
    handleDeleteCommentCallback: () => void
  ): void => {
    handleDeleteCommentCallback?.();
    setDeletedCommentIds([comment.id]);
    setRecentlyDeleted({
      comment,
      id: comment.id,
    });

    // Clear any existing undo timer
    if (undoTimer) {
      clearTimeout(undoTimer);
    }

    // Set a new timer
    const timer = setTimeout(() => {
      setRecentlyDeleted(null);
    }, 5000);

    setUndoTimer(timer);
  };

  const handleDeleteComment = (
    comment: CommentType,
    handleDeleteCommentCallback: () => void
  ) => {
    cancelReply();
    cancelEdit();
    dispatch(
      updateComment(
        comment.id,
        { is_deleted: true },
        () => handleDeleteCallback(comment, handleDeleteCommentCallback),
        false
      )
    );
  };

  async function handleUndoDelete() {
    if (!recentlyDeleted) return;

    setUndoLoading(true);
    dispatch(
      updateComment(recentlyDeleted?.comment.id, { is_deleted: false }, () => {
        setUndoLoading(false);
        setRecentlyDeleted(null);
        setDeletedCommentIds([]);
      })
    );
  }

  const formatChangeMessage = (change: string): string => {
    // Handle status changes separately
    if (change.includes("Status")) return change;

    // Extract the field name and values
    const parts = change.split(" from ");
    if (parts.length !== 2) return change;

    const [fieldPart, valuesPart] = parts;
    const field = fieldPart.replace("changed ", "").trim();
    const [oldValue, newValue] = valuesPart
      .split(" to ")
      .map((v) => v.trim().replace(/"/g, ""));

    // Format priority values
    const formatPriorityValue = (value: string): string => {
      if (value === "null") return value;
      const priority = priorityOptions.find(
        (p) => p.value.toString() === value
      );
      return priority ? priority.label : value;
    };

    // Format the message based on the values
    if (oldValue === "null" && newValue !== "null") {
      const formattedValue =
        field === "priority" ? formatPriorityValue(newValue) : newValue;
      return `set ${field} to "${formattedValue}"`;
    } else if (oldValue !== "null" && newValue === "null") {
      return `removed ${field}`;
    } else if (oldValue === "null" && newValue === "null") {
      return `updated ${field}`;
    } else {
      const formattedOldValue =
        field === "priority" ? formatPriorityValue(oldValue) : oldValue;
      const formattedNewValue =
        field === "priority" ? formatPriorityValue(newValue) : newValue;
      return `changed ${field} from "${formattedOldValue}" to "${formattedNewValue}"`;
    }
  };

  useEffect(() => {
    dispatch(getActivityLog({ action_item: actionItemId }));
    dispatch(getCommentOfSpecificActionItem({ action_item: actionItemId }));
  }, [dispatch, actionItemId]);

  return (
    <>
      <div className={styles.RightSide_heading}>
        <h3 className={styles.activeHeading}>Activity</h3>

        <div className={styles.RightSide_Search}>
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className={styles.RightSide_Search_Icon}>
            <SearchIcon />
          </div>
        </div>
      </div>

      {(activityLoading || commentOfSpecificActionItemLoading) && (
        <div className={styles.loader}>
          <Spin indicator={<LoadingOutlined spin />} size="large" />
        </div>
      )}

      <div className={styles.RightSide_Activity}>
        {filteredActivities?.length === 0 ? (
          <div className={styles.noResults}>
            <p>No activity or comments found</p>
          </div>
        ) : (
          filteredActivities?.map((item: MergedActivityItem, index: number) => {
            const userName =
              item?.user_detail?.first_name || item?.user_detail?.last_name
                ? item?.user_detail?.first_name +
                  " " +
                  item?.user_detail?.last_name
                : item?.user_detail?.name || item?.user_detail?.email;
            const isCreateActionItem =
              item.type === "activity" &&
              (item as ActivityLogType).action === "create";

            if (
              recentlyDeleted &&
              recentlyDeleted.id === (item as CommentType).id
            ) {
              return (
                <div key={item.id ?? index} className={styles.activityItem}>
                  <div className={styles.undoBar}>
                    <span>Comment Deleted</span>
                    <button
                      onClick={handleUndoDelete}
                      className={styles.undoButton}
                      disabled={undoLoading}
                    >
                      {undoLoading ? (
                        <Spin
                          indicator={<LoadingOutlined spin />}
                          size="small"
                        />
                      ) : (
                        <>
                          <UndoIcon /> Undo
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            }

            // Skip rendering if this is a deleted comment
            if (
              item.type === "comment" &&
              deletedCommentIds.includes((item as CommentType).id)
            ) {
              return null;
            }

            return (
              <div key={index} className={styles.activityItem}>
                {item.type === "comment" ? (
                  <div className={styles.commentThread}>
                    <CommentItem
                      comment={item as CommentType}
                      isReplyComment={false}
                      onReply={handleReplyToComment}
                      onEdit={handleEditComment}
                      onLike={handleLikeComment}
                      onDelete={handleDeleteComment}
                    />

                    {(() => {
                      const commentItem = item as CommentType;
                      const replies = commentItem.replies?.filter(
                        (reply) => !deletedCommentIds.includes(reply.id)
                      );
                      return replies && replies.length > 0 ? (
                        <div className={styles.repliesContainer}>
                          {replies.map((reply: CommentType) => (
                            <div key={reply.id}>
                              <CommentItem
                                comment={reply}
                                isReplyComment={true}
                                onReply={handleReplyToComment}
                                onEdit={handleEditComment}
                                onLike={handleLikeComment}
                                onDelete={handleDeleteComment}
                              />
                            </div>
                          ))}
                        </div>
                      ) : null;
                    })()}
                  </div>
                ) : (
                  <div className={styles.activityLog}>
                    <div className={styles.activityHeader}>
                      <h5 className={styles.action}>
                        {isCreateActionItem && (
                          <div className={styles.changeDetail}>
                            <span className={styles.userName}>{userName}</span>
                            created this action item
                          </div>
                        )}
                        {(() => {
                          const activityItem = item as ActivityLogType;
                          const changeDetails = activityItem.changeDetails;
                          return changeDetails && changeDetails.length > 0 ? (
                            <div className={styles.activityDetails}>
                              {changeDetails.map(
                                (change: string, i: number) => {
                                  const firstChild = i === 0;
                                  const isLastChild =
                                    i === changeDetails.length - 1;
                                  const isSecondToLast =
                                    i === changeDetails.length - 2;
                                  const isStatusChange =
                                    change.includes("Status");
                                  const isNameHtmlChange =
                                    change.includes("name_html");

                                  if (isStatusChange) {
                                    const fromStatusText = change
                                      .split("from")[1]
                                      .split("to")[0]
                                      .trim()
                                      .replace(/"/g, "");
                                    const toStatusText = change
                                      .split("to")[1]
                                      .trim()
                                      .replace(/"/g, "");

                                    const fromStatus =
                                      categoryStatusOptions.find(
                                        (status) =>
                                          status.label === fromStatusText
                                      );

                                    const toStatus = categoryStatusOptions.find(
                                      (status) => status.label === toStatusText
                                    );

                                    const FromStatusIcon = fromStatus?.icon;
                                    const ToStatusIcon = toStatus?.icon;

                                    return (
                                      <div
                                        key={i}
                                        className={styles.changeDetail}
                                      >
                                        {firstChild && (
                                          <span className={styles.userName}>
                                            {userName}
                                          </span>
                                        )}
                                        <span className={styles.statusChange}>
                                          changed Status from{" "}
                                          <span>
                                            {FromStatusIcon && (
                                              <FromStatusIcon />
                                            )}{" "}
                                            {fromStatusText}
                                          </span>{" "}
                                          to{" "}
                                          <span>
                                            {ToStatusIcon && <ToStatusIcon />}{" "}
                                            {toStatusText}{" "}
                                          </span>
                                          {!isLastChild &&
                                            (isSecondToLast ? " and " : ", ")}
                                        </span>
                                      </div>
                                    );
                                  } else if (isNameHtmlChange) {
                                    const formattedMessage =
                                      formatChangeMessage(change);
                                    return (
                                      <div
                                        key={i}
                                        className={styles.changeDetail}
                                      >
                                        {firstChild && (
                                          <span className={styles.userName}>
                                            {userName}
                                          </span>
                                        )}
                                        {formattedMessage}
                                        {!isLastChild &&
                                          (isSecondToLast ? " and " : ", ")}
                                      </div>
                                    );
                                  }
                                  {
                                    const formattedMessage =
                                      formatChangeMessage(change);
                                    return (
                                      <div
                                        key={i}
                                        className={styles.changeDetail}
                                      >
                                        {firstChild && (
                                          <span className={styles.userName}>
                                            {userName}
                                          </span>
                                        )}
                                        {formattedMessage}
                                        {!isLastChild &&
                                          (isSecondToLast ? " and " : ", ")}
                                      </div>
                                    );
                                  }
                                }
                              )}
                            </div>
                          ) : null;
                        })()}
                      </h5>
                      <span className={styles.timestamp}>
                        {formatRelativeTime(item.timestamp || "")}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className={styles.RightSide_Input}>
        {isReply && (
          <div className={styles.replyingTo}>
            Replying to <span>{replyToUser}</span>
            <button
              onClick={cancelReply}
              className={styles.cancelReplyBtn}
              aria-label="Cancel reply"
              type="button"
            >
              ×
            </button>
          </div>
        )}

        <MentionsInput
          inputRef={mentionsInputRef}
          value={commentText}
          onChange={handleMentionChange}
          onKeyUp={handleKeyPress}
          className={styles.mentionsInput}
          placeholder={
            isReply
              ? `Reply to ${replyToUser}...`
              : isEdit
              ? "Edit your comment..."
              : "Add a comment..."
          }
          disabled={loading}
          a11ySuggestionsListLabel={"Suggested mentions"}
          singleLine
          style={{
            input: {
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
        >
          <Mention
            trigger="@"
            data={projectUser?.map((user: UserDetail) => ({
              id: user?.id,
              display: user?.email || "",
            }))}
            renderSuggestion={(suggestion, search, highlightedDisplay) => (
              <div className={styles.mentionOption}>
                <div className={styles.mentionAvatar}>
                  {suggestion.display?.[0] || ""}
                </div>
                <div className={styles.mentionName}>
                  {suggestion.display || ""}
                </div>
              </div>
            )}
            className={styles.mentions__mention}
          />
        </MentionsInput>

        <div className={styles.RightSide_Input_Button}>
          {commentText.length > 0 && (
            <button
              onClick={handleSendComment}
              disabled={loading}
              type="button"
            >
              {loading ? (
                <LoadingOutlined spin />
              ) : isEdit ? (
                "Update"
              ) : isReply ? (
                "Reply"
              ) : (
                "Send"
              )}
            </button>
          )}
          {(isEdit || isReply) && (
            <button
              onClick={isEdit ? cancelEdit : cancelReply}
              className={styles.cancelButton}
              disabled={loading}
              type="button"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ActionItemComment;
